import React, { useState, useMemo } from 'react';
import DimensionHeader from '../../common/DimensionHeader';
import { AnimatedNumber, AnimatedCard, useAnimateOnMount } from '../../common/AnimationUtils';
import { useTheme } from '../../../utils/ThemeSwitcher';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  Search,
  Info,
  Layers,
  TrendingUp,
  TrendingDown,
  Target,
  Activity,
  Award,
  Users,
  BarChart3,
} from 'lucide-react';
import './ProbeDepthSection.css';
import CoachingPanel from './CoachingPanel';
import PulseBeacon from './PulseBeacon';
import { getTextOverrideForFlaggedRow } from '../../audio/FlaggedRowAudioButton';
import FlaggedRowActionsCell from '../../audio/FlaggedRowActionsCell';

/**
 * ProbeDepthSection — Improvement 4 dashboard.
 * Renders results from `check_probe_depth`.
 *
 * Continuous specificity score (0–100) per transcript, aggregated to:
 *  • Summary  — mean/median, top vs bottom enumerators, recommendations
 *  • By Variable — score distribution per text column
 *  • By Enumerator — per-collector ranking with median + gap from cohort
 *  • Flagged Rows — transcripts below threshold with signal breakdown
 */
const ProbeDepthSection = ({
  data, dataCollectorId, coachingSuggestions = [], isSidePanelExpanded, isMobile = false,
  primaryData = null, audioSettings = null, tauriApi = null, transcriptsMeta = null,
  projectId = null, userInfo = null, audioNotesDoc = null,
  onMetaUpdated = null, onAudioNotesUpdated = null,
}) => {
  const { isDarkMode } = useTheme();

  const results          = data?.overall || {};
  const overallStats     = results.overall || {};
  const byVariable       = results.by_variable || {};
  const byCollector      = results.by_collector || {};
  const flaggedRows      = results.flagged_rows || [];
  const recommendations  = results.recommendations || [];
  const parameters       = results.parameters || {};

  const [activeTab, setActiveTab]             = useState('summary');
  const [variableSearch, setVariableSearch]   = useState('');
  const [variableFilter, setVariableFilter]   = useState('__all__');
  const [severityFilter, setSeverityFilter]   = useState('__all__');
  const [collectorFilter, setCollectorFilter] = useState('__all__');
  const [flagTypeFilter, setFlagTypeFilter]   = useState('__all__');
  const [rowSearch, setRowSearch]             = useState('');

  const colors = {
    background:    isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg:        isDarkMode ? '#1a1a1dff' : '#FFFFFF',
    cardBgAlt:     isDarkMode ? '#1a1a1dff' : '#FAFAFA',
    border:        isDarkMode ? '#1F1F23' : '#E5E7EB',
    borderLight:   isDarkMode ? '#2A2A2E' : '#F3F4F6',
    text:          isDarkMode ? '#929292ff' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted:     isDarkMode ? '#6B7280' : '#9CA3AF',
    success: '#10B981',
    warning: '#F59E0B',
    error:   '#EF4444',
    accent:  isDarkMode ? '#5f5f5f' : '#a8a8a8',
  };

  // ------------------------------------------------------------- guard rails
  if (results.error) {
    return (
      <div className="pds-empty-state" style={emptyWrapStyle}>
        <div style={emptyCardStyle(colors)}>
          <XCircle size={48} style={{ color: colors.error, marginBottom: '12px' }} />
          <h3 style={emptyTitleStyle(colors)}>Probe-depth check failed</h3>
          <p style={emptyTextStyle(colors)}>{results.error}</p>
        </div>
      </div>
    );
  }

  if (Object.keys(byVariable).length === 0) {
    return (
      <div className="pds-empty-state" style={emptyWrapStyle}>
        <div style={emptyCardStyle(colors)}>
          <Target size={48} style={{ color: colors.textMuted, marginBottom: '12px' }} />
          <h3 style={emptyTitleStyle(colors)}>No probe-depth data yet</h3>
          <p style={emptyTextStyle(colors)}>
            Configure the Probe-Depth detector and run a check to see results here.
          </p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------- helpers
  const sevColor = (sev) => sev === 'high' ? colors.error
                          : sev === 'medium' ? colors.warning
                          : sev === 'low' ? '#FBBF24'
                          : colors.textMuted;

  const scoreColor = (s) => s >= 70 ? colors.success
                          : s >= 50 ? '#FBBF24'
                          : s >= 35 ? colors.warning
                          : colors.error;

  const scoreLabel = (s) => s >= 70 ? 'Strong'
                          : s >= 50 ? 'Decent'
                          : s >= 35 ? 'Thin'
                          : 'Very thin';

  const qualityScore = overallStats.quality_score ?? 100;
  const qualityTone  = scoreColor(qualityScore);

  const meanScore   = overallStats.mean_score   ?? 0;
  const medianScore = overallStats.median_score ?? 0;

  // --------------------------------------------------------------- top picks
  const lowestVars = useMemo(() =>
    Object.entries(byVariable)
      .map(([v, vd]) => ({
        variable:    v,
        mean_score:  vd.mean_score   ?? 0,
        median_score: vd.median_score ?? 0,
        flag_count:  vd.flag_count   ?? 0,
        flag_rate:   vd.flag_rate    ?? 0,
        valid:       vd.valid_responses ?? 0,
      }))
      .sort((a, b) => a.median_score - b.median_score)
      .slice(0, 5),
    [byVariable]
  );

  const enumRanking = useMemo(() =>
    Object.values(byCollector)
      .sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999)),
    [byCollector]
  );

  const filteredRows = useMemo(() => {
    let rows = flaggedRows;
    if (variableFilter  !== '__all__') rows = rows.filter(r => r.variable === variableFilter);
    if (severityFilter  !== '__all__') rows = rows.filter(r => r.severity === severityFilter);
    if (collectorFilter !== '__all__') rows = rows.filter(r => r.collector_id === collectorFilter);
    if (flagTypeFilter  !== '__all__') rows = rows.filter(r => r.flag_type === flagTypeFilter);
    if (rowSearch.trim()) {
      const q = rowSearch.trim().toLowerCase();
      rows = rows.filter(r =>
        (r.response_preview || '').toLowerCase().includes(q) ||
        (r.variable || '').toLowerCase().includes(q) ||
        (r.respondent_id || '').toString().toLowerCase().includes(q) ||
        (r.collector_id || '').toString().toLowerCase().includes(q)
      );
    }
    // Worst scores first
    return rows.slice().sort((a, b) => (a.score ?? 0) - (b.score ?? 0));
  }, [flaggedRows, variableFilter, severityFilter, collectorFilter, flagTypeFilter, rowSearch]);

  const filteredVariableRows = useMemo(() => {
    const entries = Object.entries(byVariable);
    if (!variableSearch.trim()) return entries;
    const q = variableSearch.trim().toLowerCase();
    return entries.filter(([v]) => v.toLowerCase().includes(q));
  }, [byVariable, variableSearch]);

  // --------------------------------------------------------- shared MetricCard
  const MetricCard = ({ icon: Icon, title, value, subtitle, severity, animDelay = 0 }) => {
    const sevLight = severity === 'critical' ? '#b91c1c' : severity === 'warning' ? '#b45309' : null;
    const sevDark  = severity === 'critical' ? '#f87171' : severity === 'warning' ? '#fbbf24' : null;
    const iconColor = isDarkMode ? (sevDark || '#60a5fa') : (sevLight || '#2563EB');

    return (
      <AnimatedCard delay={animDelay} translateY={12}>
        <div className="pds-metric-card" style={{
          position: 'relative', padding: '18px',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden', fontFamily: "'Outfit', -apple-system, sans-serif",
        }}>
          {severity && (
            <svg width="30" height="30" viewBox="0 0 30 30" style={{
              position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible',
            }}>
              <path d="M 28 0 L 8 0 A 8 8 0 0 0 0 8 L 0 28"
                    stroke={iconColor} strokeWidth="2.5" fill="none"
                    strokeLinecap="round" opacity={0.9} />
            </svg>
          )}
          <PulseBeacon severity={severity} isDarkMode={isDarkMode} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '5px',
              background: severity
                ? (isDarkMode ? `linear-gradient(135deg, ${iconColor}22, ${iconColor}11)`
                              : `linear-gradient(135deg, ${iconColor}18, ${iconColor}08)`)
                : 'linear-gradient(135deg, rgba(37,99,235,0.14), rgba(217,119,6,0.12))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, color: iconColor,
            }}>
              <Icon size={16} strokeWidth={1.75} />
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
              <div style={{
                fontSize: '10px', fontWeight: 500, letterSpacing: '0.16em',
                textTransform: 'lowercase', color: colors.textMuted, marginBottom: '10px',
              }}>{title}</div>
              <div className="metric-value" style={{
                fontSize: '32px', fontWeight: 500, letterSpacing: '-0.02em',
                fontVariantNumeric: 'tabular-nums', color: colors.text,
                lineHeight: 1.05, marginBottom: '6px',
              }}>
                {typeof value === 'number'
                  ? <AnimatedNumber value={value} duration={800} />
                  : value}
              </div>
              <div style={{
                fontSize: '11.5px', fontWeight: 400, color: colors.textMuted, lineHeight: 1.4,
              }}>{subtitle}</div>
            </div>
          </div>
        </div>
      </AnimatedCard>
    );
  };

  // -------------------------------------------------- score distribution bar
  const ScoreBar = ({ value, max = 100, height = 8, delay = 0 }) => {
    const ready = useAnimateOnMount(300 + delay);
    return (
      <div style={{
        height: `${height}px`, borderRadius: `${height / 2}px`,
        background: isDarkMode ? '#2A2A2E' : '#E5E7EB',
        width: '100%', overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: ready ? `${Math.min(100, (value / max) * 100)}%` : '0%',
          background: scoreColor(value),
          borderRadius: `${height / 2}px`,
          transition: 'width 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
        }} />
      </div>
    );
  };

  // =========================================================================
  // SUMMARY
  // =========================================================================
  const renderSummary = () => (
    <div>
      {/* Key metrics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
        gap: '14px', marginBottom: '24px',
      }}>
        <MetricCard
          icon={Target}
          title="quality score"
          value={qualityScore}
          subtitle={`${scoreLabel(qualityScore)} — blends mean score + flag rate`}
          severity={qualityScore < 50 ? 'critical' : qualityScore < 70 ? 'warning' : null}
          animDelay={0}
        />
        <MetricCard
          icon={Activity}
          title="mean score"
          value={meanScore}
          subtitle={`out of 100 across ${overallStats.total_responses_analyzed ?? 0} transcripts`}
          animDelay={50}
        />
        <MetricCard
          icon={BarChart3}
          title="median score"
          value={medianScore}
          subtitle={`σ = ${overallStats.stdev_score ?? 0} (range ${overallStats.min_score ?? 0}–${overallStats.max_score ?? 0})`}
          animDelay={100}
        />
        <MetricCard
          icon={AlertTriangle}
          title="below threshold"
          value={overallStats.total_flags ?? 0}
          subtitle={`${overallStats.flag_rate ?? 0}% of transcripts (threshold ${parameters.score_threshold ?? 35})`}
          severity={(overallStats.flag_rate ?? 0) > 30 ? 'critical' : (overallStats.flag_rate ?? 0) > 15 ? 'warning' : null}
          animDelay={150}
        />
      </div>

      {/* spaCy banner */}
      {parameters.using_spacy === false && (
        <div style={{
          padding: '12px 14px', borderRadius: '8px',
          background: isDarkMode ? 'rgba(96, 165, 250, 0.08)' : 'rgba(37, 99, 235, 0.06)',
          border: `1px solid ${isDarkMode ? 'rgba(96, 165, 250, 0.2)' : 'rgba(37, 99, 235, 0.15)'}`,
          marginBottom: '20px', fontSize: '12.5px', color: colors.textSecondary,
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <Info size={14} style={{ flexShrink: 0, color: '#60a5fa' }} />
          <span>
            Noun-phrase counting is using a regex fallback (~75% accuracy).
            Install <code style={{ background: isDarkMode ? '#2A2A2E' : '#F3F4F6', padding: '1px 5px', borderRadius: '3px' }}>spacy</code> + <code style={{ background: isDarkMode ? '#2A2A2E' : '#F3F4F6', padding: '1px 5px', borderRadius: '3px' }}>en_core_web_sm</code> on the backend for full accuracy.
          </span>
        </div>
      )}

      {/* Two-column: lowest variables + enumerator ranking */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: '16px', marginBottom: '24px',
      }}>
        {/* Lowest variables */}
        <AnimatedCard delay={220} translateY={12}>
        <div style={{ ...tableWrapStyle(colors), padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <TrendingDown size={14} style={{ color: colors.error }} />
            <div style={subheaderStyle(colors)}>weakest variables</div>
          </div>
          {lowestVars.length === 0 ? (
            <div style={{ ...emptyRowStyle(colors), padding: '16px' }}>No data</div>
          ) : (
            lowestVars.map((v, i) => (
              <div key={v.variable} style={{
                marginBottom: '12px', paddingBottom: '12px',
                borderBottom: `1px solid ${colors.borderLight}`,
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: '6px',
                }}>
                  <span style={{ fontSize: '12.5px', color: colors.text, fontFamily: 'monospace' }}>
                    {v.variable}
                  </span>
                  <span style={{
                    fontSize: '12px', color: scoreColor(v.median_score),
                    fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                  }}>
                    <AnimatedNumber value={v.median_score} decimals={1} duration={900} delay={i * 80} />
                  </span>
                </div>
                <ScoreBar value={v.median_score} delay={i * 80} />
                <div style={{
                  fontSize: '11px', color: colors.textMuted, marginTop: '4px',
                }}>
                  {v.flag_count}/{v.valid} flagged ({v.flag_rate}%)
                </div>
              </div>
            ))
          )}
        </div>
        </AnimatedCard>

        {/* Enumerator ranking */}
        <AnimatedCard delay={300} translateY={12}>
        <div style={{ ...tableWrapStyle(colors), padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Users size={14} style={{ color: colors.warning }} />
            <div style={subheaderStyle(colors)}>enumerator ranking (lowest median first)</div>
          </div>
          {enumRanking.length === 0 ? (
            <div style={{ ...emptyRowStyle(colors), padding: '16px' }}>No collector data</div>
          ) : (
            enumRanking.map((e, i) => (
              <div key={e.collector_id} style={{
                marginBottom: '10px', paddingBottom: '10px',
                borderBottom: `1px solid ${colors.borderLight}`,
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: '4px',
                }}>
                  <span style={{ fontSize: '12.5px', color: colors.text }}>
                    <span style={{
                      display: 'inline-block', width: '20px', textAlign: 'center',
                      color: e.rank === 1 ? colors.error : colors.textMuted,
                      fontWeight: 600,
                    }}>#{e.rank}</span>
                    {' '}{e.collector_id}
                  </span>
                  <span style={{
                    fontSize: '12px', color: scoreColor(e.median_score),
                    fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                  }}>
                    <AnimatedNumber value={e.median_score} decimals={1} duration={900} delay={i * 80} />
                  </span>
                </div>
                <ScoreBar value={e.median_score} delay={i * 80} />
                <div style={{
                  fontSize: '11px', color: colors.textMuted, marginTop: '4px',
                  display: 'flex', justifyContent: 'space-between',
                }}>
                  <span>n={e.n_transcripts}, {e.flag_count} flag{e.flag_count !== 1 ? 's' : ''}</span>
                  <span style={{
                    color: e.gap_from_cohort < 0 ? colors.error : colors.textMuted,
                  }}>
                    {e.gap_from_cohort > 0 ? '+' : ''}{e.gap_from_cohort} vs cohort
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
        </AnimatedCard>
      </div>

      <CoachingPanel
        suggestions={coachingSuggestions}
        detectorSource="probe_depth"
        isMobile={isMobile}
      />
    </div>
  );

  // =========================================================================
  // BY VARIABLE
  // =========================================================================
  const renderVariables = () => (
    <div>
      <div style={{ position: 'relative', marginBottom: '16px', maxWidth: '400px' }}>
        <Search size={14} style={searchIconStyle(colors)} />
        <input
          type="text"
          value={variableSearch}
          onChange={(e) => setVariableSearch(e.target.value)}
          placeholder="Filter variables..."
          style={searchInputStyle(colors)}
        />
      </div>

      <div style={tableWrapStyle(colors)}>
        <table style={tableStyle}>
          <thead>
            <tr style={theadRowStyle(isDarkMode)}>
              <th style={th(colors)}>Variable</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Mean</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Median</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Min</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Max</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Valid</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Flagged</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Flag rate</th>
            </tr>
          </thead>
          <tbody>
            {filteredVariableRows.length === 0 ? (
              <tr><td colSpan="8" style={emptyRowStyle(colors)}>No variables match</td></tr>
            ) : (
              filteredVariableRows.map(([variable, vd]) => (
                <tr key={variable} style={{ borderTop: `1px solid ${colors.borderLight}` }}>
                  <td style={{ ...td(colors), fontFamily: 'monospace', fontSize: '12px' }}>
                    {variable}
                  </td>
                  <td style={{ ...tdRight(colors), color: scoreColor(vd.mean_score ?? 0) }}>
                    {(vd.mean_score ?? 0).toFixed(1)}
                  </td>
                  <td style={{ ...tdRight(colors), color: scoreColor(vd.median_score ?? 0), fontWeight: 600 }}>
                    {(vd.median_score ?? 0).toFixed(1)}
                  </td>
                  <td style={tdRight(colors)}>{(vd.min_score ?? 0).toFixed(1)}</td>
                  <td style={tdRight(colors)}>{(vd.max_score ?? 0).toFixed(1)}</td>
                  <td style={tdRight(colors)}>{vd.valid_responses ?? 0}</td>
                  <td style={{ ...tdRight(colors), color: (vd.flag_count ?? 0) > 0 ? colors.error : colors.textMuted }}>
                    {vd.flag_count ?? 0}
                  </td>
                  <td style={{ ...tdRight(colors), color: (vd.flag_rate ?? 0) > 30 ? colors.error : colors.text }}>
                    {(vd.flag_rate ?? 0).toFixed(1)}%
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // =========================================================================
  // BY ENUMERATOR
  // =========================================================================
  const renderCollectors = () => (
    <div>
      <div style={tableWrapStyle(colors)}>
        <table style={tableStyle}>
          <thead>
            <tr style={theadRowStyle(isDarkMode)}>
              <th style={th(colors)}>Rank</th>
              <th style={th(colors)}>Enumerator</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>n</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Median</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Mean</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>IQR</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>vs Cohort</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Flags</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Below thr.</th>
            </tr>
          </thead>
          <tbody>
            {enumRanking.length === 0 ? (
              <tr><td colSpan="9" style={emptyRowStyle(colors)}>No collector data — make sure Data Collector ID is set</td></tr>
            ) : (
              enumRanking.map((e) => (
                <tr key={e.collector_id} style={{ borderTop: `1px solid ${colors.borderLight}` }}>
                  <td style={{
                    ...td(colors), fontWeight: 600,
                    color: e.rank === 1 ? colors.error : e.rank === enumRanking.length ? colors.success : colors.text,
                  }}>
                    #{e.rank}
                    {e.rank === 1 && <span style={{ marginLeft: '6px', fontSize: '11px', color: colors.error }}>worst</span>}
                    {e.rank === enumRanking.length && enumRanking.length > 1 && (
                      <Award size={12} style={{ marginLeft: '6px', color: colors.success, verticalAlign: 'middle' }} />
                    )}
                  </td>
                  <td style={td(colors)}>{e.collector_id}</td>
                  <td style={tdRight(colors)}>{e.n_transcripts}</td>
                  <td style={{ ...tdRight(colors), color: scoreColor(e.median_score), fontWeight: 600 }}>
                    {e.median_score.toFixed(1)}
                  </td>
                  <td style={tdRight(colors)}>{e.mean_score.toFixed(1)}</td>
                  <td style={tdRight(colors)}>{e.iqr.toFixed(1)}</td>
                  <td style={{
                    ...tdRight(colors),
                    color: e.gap_from_cohort < 0 ? colors.error : e.gap_from_cohort > 0 ? colors.success : colors.textMuted,
                    fontWeight: 600,
                  }}>
                    {e.gap_from_cohort > 0 ? '+' : ''}{e.gap_from_cohort.toFixed(1)}
                  </td>
                  <td style={{ ...tdRight(colors), color: e.flag_count > 0 ? colors.error : colors.textMuted }}>
                    {e.flag_count}
                  </td>
                  <td style={tdRight(colors)}>
                    {e.below_threshold_share.toFixed(1)}%
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div style={{
        marginTop: '12px', fontSize: '11.5px', color: colors.textMuted,
        padding: '0 4px',
      }}>
        Cohort median is {(enumRanking[0]?.cohort_median ?? 0).toFixed(1)}.
        Negative gap = below cohort = coaching candidate.
      </div>
    </div>
  );

  // =========================================================================
  // FLAGGED ROWS
  // =========================================================================
  const renderRows = () => {
    const variables  = Object.keys(byVariable);
    const collectors = Object.keys(byCollector);
    const flagTypes  = Array.from(new Set(flaggedRows.map(r => r.flag_type).filter(Boolean)));

    return (
      <div>
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '10px',
          marginBottom: '16px', alignItems: 'center',
        }}>
          <select value={variableFilter} onChange={(e) => setVariableFilter(e.target.value)} style={selectStyle(isDarkMode, colors)}>
            <option value="__all__">All variables ({variables.length})</option>
            {variables.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <select value={collectorFilter} onChange={(e) => setCollectorFilter(e.target.value)} style={selectStyle(isDarkMode, colors)}>
            <option value="__all__">All enumerators</option>
            {collectors.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={flagTypeFilter} onChange={(e) => setFlagTypeFilter(e.target.value)} style={selectStyle(isDarkMode, colors)}>
            <option value="__all__">All flag types</option>
            {flagTypes.map(ft => <option key={ft} value={ft}>{ft}</option>)}
          </select>
          <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} style={selectStyle(isDarkMode, colors)}>
            <option value="__all__">All severities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <div style={{ position: 'relative', flex: '1 1 220px', minWidth: '180px' }}>
            <Search size={14} style={searchIconStyle(colors)} />
            <input
              type="text"
              value={rowSearch}
              onChange={(e) => setRowSearch(e.target.value)}
              placeholder="Search responses, ID..."
              style={searchInputStyle(colors)}
            />
          </div>
        </div>

        <div style={tableWrapStyle(colors)}>
          <table style={tableStyle}>
            <thead>
              <tr style={theadRowStyle(isDarkMode)}>
                <th style={th(colors)}>#</th>
                <th style={th(colors)}>Respondent</th>
                <th style={th(colors)}>Enumerator</th>
                <th style={th(colors)}>Variable</th>
                <th style={{ ...th(colors), textAlign: 'right' }}>Score</th>
                <th style={{ ...th(colors), textAlign: 'right' }}>WC</th>
                <th style={th(colors)}>Signals (NP/Dom/Unit/Gen)</th>
                <th style={th(colors)}>Flag</th>
                <th style={th(colors)}>Preview</th>
                <th style={{ ...th(colors), textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr><td colSpan="10" style={emptyRowStyle(colors)}>No flagged rows match the filters</td></tr>
              ) : (
                filteredRows.map((r, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${colors.borderLight}` }}>
                    <td style={td(colors)}>{r.row_index ?? i + 1}</td>
                    <td style={td(colors)}>{r.respondent_id ?? '—'}</td>
                    <td style={td(colors)}>{r.collector_id ?? '—'}</td>
                    <td style={{ ...td(colors), fontFamily: 'monospace', fontSize: '12px' }}>
                      {r.variable}
                    </td>
                    <td style={{ ...tdRight(colors), color: scoreColor(r.score ?? 0), fontWeight: 700 }}>
                      {(r.score ?? 0).toFixed(1)}
                    </td>
                    <td style={tdRight(colors)}>{r.word_count ?? 0}</td>
                    <td style={{ ...td(colors), fontFamily: 'monospace', fontSize: '11.5px' }}>
                      {r.np_count ?? 0}/{r.domain_hits ?? 0}/{r.unit_hits ?? 0}/<span style={{ color: (r.generic_hits ?? 0) > 0 ? colors.warning : colors.textMuted }}>{r.generic_hits ?? 0}</span>
                    </td>
                    <td style={td(colors)}>
                      <span style={pillStyle(r.flag_type === 'too_short' ? colors.error : colors.warning)}>
                        {r.flag_type ?? 'flag'}
                      </span>
                    </td>
                    <td style={{ ...td(colors), maxWidth: '300px', fontSize: '12px' }}>
                      {(() => {
                        const override = getTextOverrideForFlaggedRow(
                          r.variable, r.row_index, primaryData, transcriptsMeta);
                        const displayText = override?.text ?? r.response_preview ?? '';
                        return (
                          <div style={{
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            color: colors.textSecondary, minWidth: 0,
                          }}>
                            {displayText || '(empty)'}
                            {override && (
                              <span title={`Re-transcribed${override.languageHint ? ' in ' + override.languageHint : ''}. Refresh checks to update flag.`}
                                    style={{
                                      marginLeft: '8px',
                                      padding: '1px 7px',
                                      fontSize: '9.5px',
                                      fontWeight: 600,
                                      letterSpacing: '0.04em',
                                      textTransform: 'uppercase',
                                      background: 'rgba(217,119,6,0.12)',
                                      color: '#D97706',
                                      border: '1px solid rgba(217,119,6,0.30)',
                                      borderRadius: '999px',
                                      cursor: 'help',
                                    }}>
                                re-transcribed
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ ...td(colors), textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <FlaggedRowActionsCell
                        variable={r.variable}
                        rowIndex={r.row_index}
                        submissionId={r.respondent_id}
                        primaryData={primaryData}
                        audioSettings={audioSettings}
                        tauriApi={tauriApi}
                        isDarkMode={isDarkMode}
                        transcriptsMeta={transcriptsMeta}
                        projectId={projectId}
                        userInfo={userInfo}
                        audioNotesDoc={audioNotesDoc}
                        onMetaUpdated={onMetaUpdated}
                        onAudioNotesUpdated={onAudioNotesUpdated}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div style={{
          marginTop: '10px', fontSize: '11.5px', color: colors.textMuted, padding: '0 4px',
        }}>
          Showing {filteredRows.length} of {flaggedRows.length} flagged transcripts.
          NP = noun phrases, Dom = domain words, Unit = units, Gen = generic filler.
        </div>
      </div>
    );
  };

  // =========================================================================
  // TABS + RENDER
  // =========================================================================
  const tabs = [
    { id: 'summary',    label: 'Summary' },
    { id: 'variables',  label: 'By Variable',   count: Object.keys(byVariable).length },
    { id: 'collectors', label: 'By Enumerator', count: Object.keys(byCollector).length },
    { id: 'rows',       label: 'Flagged Rows',  count: flaggedRows.length },
  ];

  return (
    <div className="pds-section" style={{
      padding: isMobile ? '12px' : '0 4px',
      boxSizing: 'border-box',
      overflow: isMobile ? 'visible' : 'hidden',
    }}>
      <DimensionHeader
        slug="probe-depth"
        title="Probe-Depth Scoring"
        description="Scores each transcript 0–100 on specificity — counts noun phrases, domain vocabulary, units of measurement, and penalizes generic filler. Aggregates per enumerator to surface coaching targets. Designed for audio-heavy qualitative surveys where the point of recording is to capture specifics that coded fields can't."
        rememberCollapseKey="probe-depth"
        stat={{
          score: qualityScore,
          scoreLabel: 'quality score',
          unit: '%',
        }}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isMobile={isMobile}
        isSidePanelExpanded={isSidePanelExpanded}
      />

      <div style={{ minHeight: '600px', marginTop: '16px' }}>
        {activeTab === 'summary'    && renderSummary()}
        {activeTab === 'variables'  && renderVariables()}
        {activeTab === 'collectors' && renderCollectors()}
        {activeTab === 'rows'       && renderRows()}
      </div>
    </div>
  );
};

// ----------------------------------------------------------- shared styles
const emptyWrapStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  minHeight: '400px', padding: '32px',
};
const emptyCardStyle = (colors) => ({
  textAlign: 'center', padding: '32px', borderRadius: '16px',
  backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`,
});
const emptyTitleStyle = (colors) => ({
  fontSize: '16px', fontWeight: 600, color: colors.text, marginBottom: '8px',
});
const emptyTextStyle = (colors) => ({ fontSize: '13px', color: colors.textSecondary });

const subheaderStyle = (colors) => ({
  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.12em',
  color: colors.textMuted, marginBottom: '4px', fontWeight: 500,
});

const th = (colors) => ({
  padding: '12px 14px', textAlign: 'left', fontWeight: 600,
  color: colors.text, fontSize: '12px',
  textTransform: 'uppercase', letterSpacing: '0.04em',
});
const td = (colors) => ({ padding: '11px 14px', color: colors.text });
const tdRight = (colors) => ({
  padding: '11px 14px', textAlign: 'right',
  fontVariantNumeric: 'tabular-nums', color: colors.text,
});

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '13px' };
const tableWrapStyle = (colors) => ({
  backgroundColor: colors.cardBg,
  border: `1px solid ${colors.border}`,
  borderRadius: '12px',
  overflow: 'hidden',
});
const theadRowStyle = (isDarkMode) => ({
  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
});
const emptyRowStyle = (colors) => ({
  padding: '32px', textAlign: 'center', color: colors.textMuted,
});

const pillStyle = (color) => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: '999px',
  fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
  background: `${color}22`, color,
});

const searchIconStyle = (colors) => ({
  position: 'absolute', left: '10px', top: '50%',
  transform: 'translateY(-50%)', color: colors.textMuted,
});
const searchInputStyle = (colors) => ({
  width: '100%', padding: '8px 12px 8px 32px',
  borderRadius: '8px', border: `1px solid ${colors.border}`,
  background: colors.cardBg, color: colors.text, fontSize: '13px',
  fontFamily: 'inherit',
});

const selectStyle = (isDarkMode, colors) => ({
  padding: '8px 12px', borderRadius: '8px',
  border: `1px solid ${colors.border}`,
  background: colors.cardBg, color: colors.text, fontSize: '13px',
  cursor: 'pointer', fontFamily: 'inherit', appearance: 'none',
  backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path fill='${isDarkMode ? '%239CA3AF' : '%236B7280'}' d='M6 9L1 4h10z'/></svg>")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', paddingRight: '28px',
});

export default ProbeDepthSection;